import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { faStore } from "@fortawesome/free-solid-svg-icons";
import { DashCard, DashCardHeader, DashCardBody } from "./v2/DashCard";
import { formatCurrency } from "utils/formatters";

interface SalesByDepartment {
  department: string;
  value: number;
  lastMonth: number;
  growth: number;
}

interface SalesByDepartmentResponse {
  data: {
    departments: SalesByDepartment[];
  };
}

const SalesByDepartmentChart: React.FC = () => {
  const [period] = useState<"week" | "month" | "quarter" | "year">("quarter");

  const fetchSalesByDepartment = async (): Promise<SalesByDepartmentResponse> => {
    const response = await axios.get<SalesByDepartmentResponse>(
      `/api/SalesArea/sales-by-department?period=${period}`,
    );
    return response.data;
  };

  const { getQueryRetryHandler } = usePermissionModal();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["salesByDepartment", period],
    queryFn: fetchSalesByDepartment,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["salesByDepartment", period],
      defaultMessage: "Você não possui permissão para visualizar as vendas por departamento.",
      showModal: false,
    }),
  });

  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;
  if (isAccessDenied) return null;

  const salesByDepartment = !isLoading && !isError && data?.data?.departments ? data.data.departments : [];
  const maiorValor = Math.max(1, ...salesByDepartment.map((d) => d.value));

  const renderPlaceholderRows = () =>
    Array(6)
      .fill(0)
      .map((_, index) => (
        <tr key={index}>
          <td className="dash-skeleton" style={{ height: 14 }} />
          <td className="dash-skeleton" style={{ height: 14 }} />
          <td className="dash-skeleton" style={{ height: 14 }} />
          <td className="dash-skeleton" style={{ height: 14 }} />
        </tr>
      ));

  return (
    <DashCard hoverable={false}>
      <DashCardHeader icon={faStore} variant="accent" title="Vendas por Departamento" subtitle="Último trimestre" />
      <DashCardBody flush>
        <table className="dash-table">
          <thead>
            <tr>
              <th>Departamento</th>
              <th className="dash-num">Vendas</th>
              <th className="dash-num">Crescimento</th>
              <th>Participação</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderPlaceholderRows()
            ) : isError || salesByDepartment.length === 0 ? (
              <tr>
                <td colSpan={4} className="dash-empty" style={{ padding: "2rem" }}>
                  {isError ? "Erro ao carregar dados" : "Nenhum departamento encontrado"}
                </td>
              </tr>
            ) : (
              salesByDepartment.map((dept, index) => (
                <tr key={index}>
                  <td className="dash-table-primary">{dept.department}</td>
                  <td className="dash-num">{formatCurrency(dept.value)}</td>
                  <td className="dash-num">
                    <span className={`dash-pill ${dept.growth >= 0 ? "dash-pill--good" : "dash-pill--critical"}`}>
                      {dept.growth >= 0 ? "+" : ""}
                      {dept.growth}%
                    </span>
                  </td>
                  <td style={{ width: 120 }}>
                    <div style={{ height: 6, borderRadius: 999, background: "var(--dash-border-soft)", overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          borderRadius: 999,
                          background: dept.growth >= 0 ? "var(--dash-good)" : "var(--dash-critical)",
                          width: `${(dept.value / maiorValor) * 100}%`,
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DashCardBody>
    </DashCard>
  );
};

export default SalesByDepartmentChart;
